import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { find, get, some } from 'lodash';
import requester from 'utils/requester';
import { getIsLifecellFlag, getIsLoggedInSelector } from 'reducers/profile/selectors';
import DASHBOARD_TAGS_ALLOWED from 'components/Menu/dashboard-tag-url';
import { selectMenuData } from 'reducers/menu/selectors';
import { CONTENT_TYPES } from 'constants/content-types';
import urlHelper, { resolvePlayerNavLink } from 'helpers/urlhelper';
import api from 'config/api';
import { MISSING_SUBSCRIPTION } from 'constants/visibility-types';
import useModals from 'hooks/useModals';
import { selectIsParentalControlEnabled } from 'reducers/parental-lock/selectors';
import { navigateToPlayerPage } from 'actions/player';
import PLAYER_TYPES from 'constants/player-types';

const INNER_LINKS_ALLOWED_ROUTES = [
  'dashboard',
  'subdashboard',
  'asset',
  'series',
  'channel',
  'collection',
  'search',
  'promocode',
  'packages',
  'program',
];

export default () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const menu = useSelector(selectMenuData);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const isLifecellUser = useSelector(getIsLifecellFlag);

  const { showEnterPinModal, showLifecellPaymentModal } = useModals();

  const fetchChannelInfo = useCallback(
    async (channelID) => {
      const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.channel, isLoggedIn);
      const channel = await requester({
        method: 'get',
        url: `${api.host}${endpoint}/${channelID}`,
      });
      return channel;
    },
    [isLoggedIn],
  );

  const fetchProgramInfo = useCallback(
    async (programID) => {
      const endpoint = urlHelper.getAuthorizedEndpointUrl(api.endpoints.program, isLoggedIn);
      return await requester({
        method: 'get',
        url: `${api.host}${endpoint}/${programID}`,
      });
    },
    [isLoggedIn],
  );

  const onEnter = useCallback(
    async ({ detailsUrl = '' }, focusBackConfig) => {
      if (detailsUrl.indexOf('volia://') === -1) return;
      const innerLink = detailsUrl.split('volia://')[1];
      if (!innerLink) return;

      const innerLinkRoute = innerLink.split('/')[0];
      let route = '';

      if (INNER_LINKS_ALLOWED_ROUTES.indexOf(innerLinkRoute) === -1) return;

      switch (innerLinkRoute) {
        case 'dashboard':
          const dashboardTag = innerLink.split('/').pop();
          if (Object.keys(DASHBOARD_TAGS_ALLOWED).indexOf(dashboardTag) === -1) return;
          route = DASHBOARD_TAGS_ALLOWED[dashboardTag];
          break;
        case 'subdashboard':
          const parentDashboardTag = innerLink.split('/')[1];
          if (Object.keys(DASHBOARD_TAGS_ALLOWED).indexOf(parentDashboardTag) === -1) return;
          const subDashboardsByParentTag = get(find(menu, { dashboardTag: parentDashboardTag }), 'subDashboards', []);
          if (subDashboardsByParentTag.length) {
            const subDashboardIdFromInnerLink = innerLink.split('/').pop();
            const isSubDashboardExists = some(subDashboardsByParentTag, {
              id: parseInt(subDashboardIdFromInnerLink, 10),
            });
            if (isSubDashboardExists) {
              route = `/${parentDashboardTag.toLowerCase()}/${subDashboardIdFromInnerLink}`;
            } else {
              console.error(
                `DashboardTag=${parentDashboardTag} has no subdashboards with ID=${subDashboardIdFromInnerLink}`,
              );
            }
          } else {
            console.error(`DashboardTag=${parentDashboardTag} has no subdashboards`);
          }
          break;
        case 'asset':
          const assetID = innerLink.split('/')[1];
          if (!assetID) return;
          route = `/details/${CONTENT_TYPES.VOD_ASSET}/${assetID}`;
          break;
        case 'series':
          const seriesID = innerLink.split('/')[1];
          if (!seriesID) return;
          route = `/details/${CONTENT_TYPES.TV_SERIES}/${seriesID}`;
          break;
        case 'channel':
          const channelID = innerLink.split('/')[1];
          if (!channelID) return;

          try {
            const channel = await fetchChannelInfo(channelID);
            const isAdult = get(channel.pgRating, ['minRecomendedAge'], 0) === 18;

            const goToPlayer = () => {
              dispatch(
                navigateToPlayerPage({
                  item: { id: channel.id },
                  type: PLAYER_TYPES.CHANNEL,
                }),
              );
            };

            if (channel.visibilityDetails === MISSING_SUBSCRIPTION) {
              const onSuccess = resolvePlayerNavLink(
                { id: channel.id },
                { type: PLAYER_TYPES.CHANNEL, isVodChannel: !!channel.isVod },
              );

              if (isLifecellUser) {
                showLifecellPaymentModal({ id: channelID, type: 'channelId', onSuccess: goToPlayer }, focusBackConfig);
                return;
              }
              return history.push({
                pathname: '/profile/packages/filter',
                search: `?channelId=${channelID}&onSuccess=${onSuccess}`,
              });
            }

            if (isParentalEnabled && isAdult) {
              return showEnterPinModal(focusBackConfig, goToPlayer);
            }

            if (isLoggedIn) {
              return goToPlayer();
            } else {
              history.push('/player/channel');
            }
          } catch {
            history.push('/player/channel');
          }

          break;
        case 'program':
          const channelIDFromLink = innerLink.split('/')[1];
          const programID = innerLink.split('/')[2];

          //Если иннерлика не полная - ошибка редакции, игнорируем клик
          if (!channelIDFromLink || !programID) return;

          // Ид вводятся вручную, редакция может ошибиться, поэтому таким образом игнорируем 404ю
          const promisesInfo = await Promise.allSettled([
            fetchChannelInfo(channelIDFromLink),
            fetchProgramInfo(programID),
          ]);

          const isChannelExists = promisesInfo[0].status === 'fulfilled';
          const isProgramExists = promisesInfo[1].status === 'fulfilled';

          //Если редакция проставила несуществующий канал, то просто кидаем в ТВ раздел
          if (!isChannelExists) return history.push('/player/channel');

          const channel = promisesInfo[0].value;
          const program = promisesInfo[1].value;

          const programStartTime = (isProgramExists && program?.catchup && program?.startTime) || null;

          const isAdult = get(channel.pgRating, ['minRecomendedAge'], 0) === 18;

          const itemToNavigate = { id: channel.id };
          if (isProgramExists && programStartTime) {
            itemToNavigate.metadata = {
              startTime: programStartTime,
            };
          }

          const goToPlayer = () => {
            dispatch(
              navigateToPlayerPage({
                item: itemToNavigate,
                type: PLAYER_TYPES.CHANNEL,
              }),
            );
          };

          if (channel.visibilityDetails === MISSING_SUBSCRIPTION) {
            const onSuccess = resolvePlayerNavLink(itemToNavigate, {
              type: PLAYER_TYPES.CHANNEL,
              isVodChannel: !!channel.isVod,
            });

            if (isLifecellUser) {
              showLifecellPaymentModal({ id: channelID, type: 'channelId', onSuccess: goToPlayer }, focusBackConfig);
              return;
            }
            return history.push({
              pathname: '/profile/packages/filter',
              search: `?channelId=${channel.id}&onSuccess=${onSuccess}`,
            });
          }

          if (isParentalEnabled && isAdult) {
            return showEnterPinModal(focusBackConfig, goToPlayer);
          }

          goToPlayer();

          break;
        case 'collection':
          const collectionID = innerLink.split('/').pop();
          route = `/list-grid/${collectionID}`;
          break;
        case 'search':
          const searchQuery = innerLink.split('/').pop();
          route = `/search?query=${searchQuery}`;
          break;
        case 'promocode':
          const promocode = innerLink.split('/').pop();
          route = `/profile/promocode/${promocode}`;
          break;
        case 'packages':
          route = '/profile/all-packages';
          break;
        default:
        // nothing
      }

      if (route) history.push(route);
    },
    [
      history,
      menu,
      fetchChannelInfo,
      fetchProgramInfo,
      isParentalEnabled,
      isLoggedIn,
      dispatch,
      isLifecellUser,
      showLifecellPaymentModal,
      showEnterPinModal,
    ],
  );

  return onEnter;
};
